import { useMemo } from 'react';
import type { ClientSummary } from '../../shared/types';
import { accentTokens, parseHex } from '../../shared/branding';
import { BrandLogo } from './AppShell';

/**
 * Freigabe-Seiten in der Farbe des Kunden: Die Akzentfarbe der Instanz wird für die Dauer der
 * Seite durch die Kundenfarbe ersetzt (Linien, Links, Hervorhebungen). Schrift und Flächen bleiben.
 */
export function ClientTheme({ color }: { color: string | null }) {
  const css = useMemo(() => {
    if (!color || !parseHex(color)) return '';
    const ink = getComputedStyle(document.documentElement).getPropertyValue('--ink').trim() || '#16181d';
    const vars = Object.entries(accentTokens(color, ink))
      .map(([k, v]) => `${k}: ${v};`)
      .join(' ');
    return `html:root { ${vars} }`;
  }, [color]);
  return css ? <style>{css}</style> : null;
}

/** Wer die Freigabe präsentiert: die Agentur dieser Instanz, ein Partner oder niemand. */
function Presenter({ client }: { client: ClientSummary }) {
  const { presenter } = client;
  if (presenter.mode === 'none') return null;
  if (presenter.mode === 'partner') {
    if (!presenter.logoUrl && !presenter.name) return null;
    return (
      <div className="client-presenter">
        <span className="tiny muted nowrap">präsentiert von</span>
        {presenter.logoUrl ? (
          <img className="presenter-logo" src={presenter.logoUrl} alt={presenter.name} />
        ) : (
          <strong>{presenter.name}</strong>
        )}
      </div>
    );
  }
  return (
    <div className="client-presenter">
      <span className="tiny muted nowrap">präsentiert von</span>
      <BrandLogo style={{ height: 24, maxWidth: 150 }} />
    </div>
  );
}

/**
 * Kopf der Kundenseite: das Logo des Kunden groß, dazu Kampagne und Absender. Die Plattform
 * selbst erscheint nur im Fuß – im Vordergrund stehen Empfänger und Absender.
 */
export function ClientBrandHeader({ client, campaignName }: { client: ClientSummary; campaignName: string }) {
  return (
    <header className="client-head">
      <div className="client-head-inner">
        <div className="client-brand">
          {client.logoUrl ? (
            <img className="client-logo" src={client.logoUrl} alt={client.name} />
          ) : (
            <strong className="client-wordmark">{client.name}</strong>
          )}
        </div>
        {campaignName && (
          <div className="client-head-text">
            <span className="eyebrow">Zur Freigabe</span>
            <strong>{campaignName}</strong>
          </div>
        )}
        <Presenter client={client} />
      </div>
    </header>
  );
}
